import { useLayoutEffect, useRef } from 'preact/hooks';
import { flightPath, type Point, passFraction } from '../lib/flight';
import { useController } from '../state/context';

const center = (el: Element): Point => {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
};

/**
 * Слой поверх экрана, по которому летят «проценты нагрева» (`.hf-chip`) и остаётся светящийся след (`.hf-trail`).
 * Всё создаётся императивно через Web Animations: координаты плиток известны только в момент хода.
 * Слой декоративный (aria-hidden): смысл дублируют подсказка под полем и запись в истории.
 */
export function HeatFlight() {
  const c = useController();
  const layer = useRef<HTMLDivElement>(null);
  const flight = c.fx.value.flight;

  useLayoutEffect(() => {
    const root = layer.current;
    if (!flight || !root) return;
    const from = document.querySelector(`[data-index="${flight.pick}"]`);
    const bombTile = document.querySelector('[data-testid="tile"][data-role="b"]');
    const meterIcon = document.querySelector('.mc-bomb');
    if (!from || !bombTile || !meterIcon) return;

    const a = center(from);
    const bomb = center(bombTile);
    const meter = center(meterIcon);
    const timers: number[] = [];
    const nodes: HTMLElement[] = [];

    flight.parts.forEach((part, k) => {
      const delay = k * 240;
      const dur = flight.ms - delay - 120;
      // «услышала» — через плитку бомбы; штраф за обманку — напрямую в шкалу
      const via = part.kind === 'heard' ? [a, bomb, meter] : [a, meter];
      const d = flightPath(via);

      // светящийся след
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', `hf-trail ${part.kind}`);
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', d);
      path.setAttribute('pathLength', '1');
      svg.appendChild(path);
      root.appendChild(svg);
      path.animate(
        [
          { strokeDashoffset: '1', opacity: 1 },
          { strokeDashoffset: '0', opacity: 1, offset: 0.85 },
          { strokeDashoffset: '0', opacity: 0 },
        ],
        { duration: dur + 380, delay, easing: 'cubic-bezier(.3,.1,.4,1)', fill: 'both' },
      );

      // сам процент
      const chip = document.createElement('div');
      chip.className = `hf-chip ${part.kind}`;
      chip.textContent = part.text;
      chip.style.offsetPath = `path("${d}")`;
      root.appendChild(chip);
      nodes.push(chip);
      const anim = chip.animate(
        [
          { offsetDistance: '0%', opacity: 0, transform: 'scale(.5)' },
          { offsetDistance: '6%', opacity: 1, transform: 'scale(1.15)', offset: 0.1 },
          { offsetDistance: '92%', opacity: 1, transform: 'scale(1)', offset: 0.9 },
          { offsetDistance: '100%', opacity: 0, transform: 'scale(.35)' },
        ],
        { duration: dur, delay, easing: 'cubic-bezier(.4,.05,.35,1)', fill: 'both' },
      );
      anim.finished.then(() => chip.remove()).catch(() => {});

      // удар по плитке бомбы в тот момент, когда процент до неё долетел
      if (part.kind === 'heard') {
        const hitAt = delay + dur * passFraction(a, bomb, meter) * 0.92;
        timers.push(
          window.setTimeout(() => {
            bombTile.animate(
              [
                { transform: 'scale(1)' },
                { transform: 'scale(1.12) rotate(-3deg)', offset: 0.35 },
                { transform: 'scale(.97) rotate(2deg)', offset: 0.65 },
                { transform: 'scale(1)' },
              ],
              { duration: 480, easing: 'ease-out' },
            );
            const ring = document.createElement('i');
            ring.className = 'hf-ring';
            const r = bombTile.getBoundingClientRect();
            ring.style.left = `${r.left + r.width / 2}px`;
            ring.style.top = `${r.top + r.height / 2}px`;
            root.appendChild(ring);
            ring
              .animate(
                [
                  { transform: 'translate(-50%,-50%) scale(.3)', opacity: 0.9 },
                  { transform: 'translate(-50%,-50%) scale(1.9)', opacity: 0 },
                ],
                { duration: 620, easing: 'ease-out' },
              )
              .finished.then(() => ring.remove())
              .catch(() => {});
          }, hitAt),
        );
      }

      // приземление на шкалу: бомба у шкалы вздрагивает
      timers.push(
        window.setTimeout(
          () => {
            meterIcon.animate(
              [
                { transform: 'scale(1)' },
                { transform: 'scale(1.4)', offset: 0.4 },
                { transform: 'scale(1)' },
              ],
              { duration: 420, easing: 'ease-out' },
            );
          },
          delay + dur * 0.95,
        ),
      );
    });

    return () => {
      for (const t of timers) clearTimeout(t);
      for (const n of nodes) n.remove();
      root.replaceChildren();
    };
  }, [flight]);

  return <div class="hf" ref={layer} aria-hidden="true" data-testid="heat-flight" />;
}
